import { api } from "../api";
// Mensagens são exibidas centralmente via interceptor em service/api
import {
  Link,
  LinksResponse,
  CreateLinkRequest,
  CustomTrafficSource,
  CreateCustomTrafficSourceRequest,
} from "../../types/integrations";

let showcaseCache: any | null = null;
let showcaseCacheAt = 0;
let pendingShowcaseRequest: Promise<any> | null = null;
let showcaseProfileCache: any | null = null;
let showcaseProfileCacheAt = 0;
let pendingShowcaseProfileRequest: Promise<any> | null = null;
let showcaseSocialMediaCache: any | null = null;
let showcaseSocialMediaCacheAt = 0;
let pendingShowcaseSocialMediaRequest: Promise<any> | null = null;
let showcaseBlocksCache: any | null = null;
let showcaseBlocksCacheAt = 0;
let pendingShowcaseBlocksRequest: Promise<any> | null = null;
let customTrafficSourcesCache: CustomTrafficSource[] | null = null;
let customTrafficSourcesCacheAt = 0;
let pendingCustomTrafficSourcesRequest: Promise<CustomTrafficSource[]> | null =
  null;
const SHOWCASE_CACHE_TTL_MS = 30000;
const CUSTOM_TRAFFIC_SOURCES_CACHE_TTL_MS = 30000;

const invalidateShowcaseCaches = (): void => {
  showcaseCache = null;
  showcaseCacheAt = 0;
  pendingShowcaseRequest = null;
  showcaseProfileCache = null;
  showcaseProfileCacheAt = 0;
  pendingShowcaseProfileRequest = null;
  showcaseSocialMediaCache = null;
  showcaseSocialMediaCacheAt = 0;
  pendingShowcaseSocialMediaRequest = null;
  showcaseBlocksCache = null;
  showcaseBlocksCacheAt = 0;
  pendingShowcaseBlocksRequest = null;
};

const invalidateCustomTrafficSourcesCache = (): void => {
  customTrafficSourcesCache = null;
  customTrafficSourcesCacheAt = 0;
  pendingCustomTrafficSourcesRequest = null;
};

// Endpoints da Vitrine
export const showcaseAPI = {
  // Verificar se a conta possui uma vitrine
  getShowcase: async () => {
    if (showcaseCache && Date.now() - showcaseCacheAt < SHOWCASE_CACHE_TTL_MS) {
      return showcaseCache;
    }

    if (pendingShowcaseRequest) {
      return pendingShowcaseRequest;
    }

    pendingShowcaseRequest = api.get(`/showcase`).then((response) => {
      showcaseCache = response;
      showcaseCacheAt = Date.now();
      return response;
    });

    try {
      return await pendingShowcaseRequest;
    } finally {
      pendingShowcaseRequest = null;
    }
  },

  // Criar nova vitrine
  createShowcase: async (data: {
    slug: string;
    defaultTheme: string;
    activeTheme?: string | null;
  }) => {
    const response = await api.post("/showcase", data);
    invalidateShowcaseCaches();
    return response;
  },

  // Atualizar vitrine completa
  updateShowcase: async (data: {
    slug?: string;
    defaultTheme?: string;
    activeTheme?: string | null;
  }) => {
    const response = await api.patch(`/showcase`, data);
    invalidateShowcaseCaches();
    return response;
  },

  // Deletar vitrine
  deleteShowcase: async () => {
    const response = await api.delete(`/showcase`);
    invalidateShowcaseCaches();
    return response;
  },

  // Verificar se existe perfil da vitrine
  getShowcaseProfile: async () => {
    if (
      showcaseProfileCache &&
      Date.now() - showcaseProfileCacheAt < SHOWCASE_CACHE_TTL_MS
    ) {
      return showcaseProfileCache;
    }

    if (pendingShowcaseProfileRequest) {
      return pendingShowcaseProfileRequest;
    }

    pendingShowcaseProfileRequest = api
      .get(`/showcase/profile`)
      .then((response) => {
        showcaseProfileCache = response;
        showcaseProfileCacheAt = Date.now();
        return response;
      });

    try {
      return await pendingShowcaseProfileRequest;
    } finally {
      pendingShowcaseProfileRequest = null;
    }
  },

  // Criar/atualizar perfil da vitrine
  // `image` é opcional: sem foto o campo não vai no payload.
  createShowcaseProfile: async (data: {
    name: string;
    username: string;
    image?: string;
    bio: string;
  }) => {
    const response = await api.post(`/showcase/profile`, data);
    invalidateShowcaseCaches();
    return response;
  },

  // Atualizar perfil da vitrine
  updateShowcaseProfile: async (data: {
    name?: string;
    username?: string;
    image?: string;
    bio?: string;
  }) => {
    const response = await api.patch(`/showcase/profile`, data);
    invalidateShowcaseCaches();
    return response;
  },

  // Verificar redes sociais da vitrine
  getShowcaseSocialMedia: async () => {
    if (
      showcaseSocialMediaCache &&
      Date.now() - showcaseSocialMediaCacheAt < SHOWCASE_CACHE_TTL_MS
    ) {
      return showcaseSocialMediaCache;
    }

    if (pendingShowcaseSocialMediaRequest) {
      return pendingShowcaseSocialMediaRequest;
    }

    pendingShowcaseSocialMediaRequest = api
      .get(`/showcase/social-media`)
      .then((response) => {
        showcaseSocialMediaCache = response;
        showcaseSocialMediaCacheAt = Date.now();
        return response;
      });

    try {
      return await pendingShowcaseSocialMediaRequest;
    } finally {
      pendingShowcaseSocialMediaRequest = null;
    }
  },

  // Criar rede social da vitrine
  createShowcaseSocialMedia: async (data: {
    platform: string;
    url: string;
  }) => {
    const response = await api.post(`/showcase/social-media`, data);
    invalidateShowcaseCaches();
    return response;
  },

  // Atualizar rede social da vitrine
  updateShowcaseSocialMedia: async (
    socialMediaId: string,
    data: {
      platform?: string;
      url?: string;
    },
  ) => {
    const response = await api.patch(
      `/showcase/social-media/${socialMediaId}`,
      data,
    );
    invalidateShowcaseCaches();
    return response;
  },

  // Deletar rede social da vitrine
  deleteShowcaseSocialMedia: async (socialMediaId: string) => {
    const response = await api.delete(
      `/showcase/social-media/${socialMediaId}`,
    );
    invalidateShowcaseCaches();
    return response;
  },

  // Listar blocos da vitrine
  getShowcaseBlocks: async () => {
    if (
      showcaseBlocksCache &&
      Date.now() - showcaseBlocksCacheAt < SHOWCASE_CACHE_TTL_MS
    ) {
      return showcaseBlocksCache;
    }

    if (pendingShowcaseBlocksRequest) {
      return pendingShowcaseBlocksRequest;
    }

    pendingShowcaseBlocksRequest = api
      .get(`/showcase/blocks`)
      .then((response) => {
        showcaseBlocksCache = response;
        showcaseBlocksCacheAt = Date.now();
        return response;
      });

    try {
      return await pendingShowcaseBlocksRequest;
    } finally {
      pendingShowcaseBlocksRequest = null;
    }
  },

  // Atualizar posições dos blocos
  updateBlocksPosition: async (data: {
    data: Array<{ id: string; entity: string }>;
  }) => {
    const response = await api.put(`/showcase/blocks/update-position`, data);
    invalidateShowcaseCaches();
    return response;
  },

  // Listar banners
  getBanners: (params?: { archived?: boolean }) => {
    const queryParams = params?.archived ? `?archived=true` : "";
    return api.get(`/showcase/banner${queryParams}`);
  },

  // Listar botões
  getButtons: (params?: { archived?: boolean }) => {
    const queryParams = params?.archived ? `?archived=true` : "";
    return api.get(`/showcase/button${queryParams}`);
  },

  // Listar galerias
  getGalleries: (params?: { archived?: boolean }) => {
    const queryParams = params?.archived ? `?archived=true` : "";
    return api.get(`/showcase/gallery${queryParams}`);
  },

  // Listar produtos de uma galeria
  getProducts: (galleryId: string, params?: { archived?: boolean }) => {
    const queryParams = params?.archived ? `?archived=true` : "";
    return api.get(`/showcase/gallery/${galleryId}/product${queryParams}`);
  },

  // Endpoints para blocos de vídeo
  createVideoBlock: async (data: { videoUrl: string }) => {
    const response = await api.post(`/showcase/video`, data);
    invalidateShowcaseCaches();
    return response;
  },

  updateVideoBlock: async (videoId: string, data: { videoUrl?: string }) => {
    const response = await api.patch(`/showcase/video/${videoId}`, data);
    invalidateShowcaseCaches();
    return response;
  },

  deleteVideoBlock: async (videoId: string) => {
    const response = await api.delete(`/showcase/video/${videoId}`);
    invalidateShowcaseCaches();
    return response;
  },

  getVideoBlock: (videoId: string) => api.get(`/showcase/video/${videoId}`),

  // Endpoints para blocos de texto
  createTextBlock: async (data: { text: string }) => {
    const response = await api.post(`/showcase/text`, data);
    invalidateShowcaseCaches();
    return response;
  },

  updateTextBlock: async (textId: string, data: { text?: string }) => {
    const response = await api.patch(`/showcase/text/${textId}`, data);
    invalidateShowcaseCaches();
    return response;
  },

  deleteTextBlock: async (textId: string) => {
    const response = await api.delete(`/showcase/text/${textId}`);
    invalidateShowcaseCaches();
    return response;
  },

  getTextBlock: (textId: string) => api.get(`/showcase/text/${textId}`),

  // Endpoints para blocos de botão
  createButtonBlock: async (data: {
    text: string;
    url: string;
    interactionId?: string;
  }) => {
    const response = await api.post(`/showcase/button`, data);
    invalidateShowcaseCaches();
    return response;
  },

  updateButtonBlock: async (
    buttonId: string,
    data: { text?: string; url?: string; interactionId?: string },
  ) => {
    const response = await api.patch(`/showcase/button/${buttonId}`, data);
    invalidateShowcaseCaches();
    return response;
  },

  deleteButtonBlock: async (buttonId: string) => {
    const response = await api.delete(`/showcase/button/${buttonId}`);
    invalidateShowcaseCaches();
    return response;
  },

  getButtonBlock: (buttonId: string) => api.get(`/showcase/button/${buttonId}`),

  // Archive/Unarchive endpoints para botões
  archiveButtonBlock: async (buttonId: string) => {
    const response = await api.patch(`/showcase/button/${buttonId}/archive`, {});
    invalidateShowcaseCaches();
    return response;
  },

  unarchiveButtonBlock: async (buttonId: string) => {
    const response = await api.patch(
      `/showcase/button/${buttonId}/unarchive`,
      {},
    );
    invalidateShowcaseCaches();
    return response;
  },

  // Endpoints para blocos de banner
  createBannerBlock: async (data: {
    image: string;
    url: string;
    interactionId?: string;
  }) => {
    const response = await api.post(`/showcase/banner`, data);
    invalidateShowcaseCaches();
    return response;
  },

  updateBannerBlock: async (
    bannerId: string,
    data: { interactionId?: string; image?: string; url?: string },
  ) => {
    const response = await api.patch(`/showcase/banner/${bannerId}`, data);
    invalidateShowcaseCaches();
    return response;
  },

  deleteBannerBlock: async (bannerId: string) => {
    const response = await api.delete(`/showcase/banner/${bannerId}`);
    invalidateShowcaseCaches();
    return response;
  },

  getBannerBlock: (bannerId: string) => api.get(`/showcase/banner/${bannerId}`),

  // Archive/Unarchive endpoints para banners
  archiveBannerBlock: async (bannerId: string) => {
    const response = await api.patch(`/showcase/banner/${bannerId}/archive`, {});
    invalidateShowcaseCaches();
    return response;
  },

  unarchiveBannerBlock: async (bannerId: string) => {
    const response = await api.patch(
      `/showcase/banner/${bannerId}/unarchive`,
      {},
    );
    invalidateShowcaseCaches();
    return response;
  },

  // Endpoints para galerias de produtos
  createGallery: async (data: {
    title: string;
    type?: string;
    interactionId?: string;
  }) => {
    const response = await api.post(`/showcase/gallery`, data);
    invalidateShowcaseCaches();
    return response;
  },

  updateGallery: async (
    galleryId: string,
    data: { title?: string; type?: string; interactionId?: string },
  ) => {
    const response = await api.patch(`/showcase/gallery/${galleryId}`, data);
    invalidateShowcaseCaches();
    return response;
  },

  deleteGallery: async (galleryId: string) => {
    const response = await api.delete(`/showcase/gallery/${galleryId}`);
    invalidateShowcaseCaches();
    return response;
  },

  getGallery: (galleryId: string) => api.get(`/showcase/gallery/${galleryId}`),

  // Archive/Unarchive endpoints para galerias
  archiveGallery: async (galleryId: string) => {
    const response = await api.patch(
      `/showcase/gallery/${galleryId}/archive`,
      {},
    );
    invalidateShowcaseCaches();
    return response;
  },

  unarchiveGallery: async (galleryId: string) => {
    const response = await api.patch(
      `/showcase/gallery/${galleryId}/unarchive`,
      {},
    );
    invalidateShowcaseCaches();
    return response;
  },

  // Endpoints para produtos dentro da galeria
  createProduct: async (
    galleryId: string,
    data: {
      name: string;
      description: string;
      url: string;
      image?: string;
      interactionId?: string;
      imageInteractionId?: string;
    },
  ) => {
    const response = await api.post(
      `/showcase/gallery/${galleryId}/product`,
      data,
    );
    invalidateShowcaseCaches();
    return response;
  },

  updateProduct: async (
    galleryId: string,
    productId: string,
    data: {
      name?: string;
      description?: string;
      url?: string;
      image?: string;
      interactionId?: string;
      imageInteractionId?: string;
    },
  ) => {
    const response = await api.patch(
      `/showcase/gallery/${galleryId}/product/${productId}`,
      data,
    );
    invalidateShowcaseCaches();
    return response;
  },

  deleteProduct: async (galleryId: string, productId: string) => {
    const response = await api.delete(
      `/showcase/gallery/${galleryId}/product/${productId}`,
    );
    invalidateShowcaseCaches();
    return response;
  },

  getProduct: (galleryId: string, productId: string) =>
    api.get(`/showcase/gallery/${galleryId}/product/${productId}`),

  // Archive/Unarchive endpoints para produtos
  archiveProduct: async (galleryId: string, productId: string) => {
    const response = await api.patch(
      `/showcase/gallery/${galleryId}/product/${productId}/archive`,
      {},
    );
    invalidateShowcaseCaches();
    return response;
  },

  unarchiveProduct: async (galleryId: string, productId: string) => {
    const response = await api.patch(
      `/showcase/gallery/${galleryId}/product/${productId}/unarchive`,
      {},
    );
    invalidateShowcaseCaches();
    return response;
  },

  // Endpoints para itens de mídia dentro da galeria
  getGalleryMedia: (galleryId: string, params?: { archived?: boolean }) => {
    const queryParams = params?.archived ? `?archived=true` : "";
    return api.get(`/showcase/gallery/${galleryId}/media${queryParams}`);
  },

  createGalleryMedia: async (
    galleryId: string,
    data: {
      image?: string;
      link?: string;
      videoUrl?: string;
      interactionId?: string;
    },
  ) => {
    const response = await api.post(
      `/showcase/gallery/${galleryId}/media`,
      data,
    );
    invalidateShowcaseCaches();
    return response;
  },

  updateGalleryMedia: async (
    galleryId: string,
    mediaId: string,
    data: {
      image?: string;
      link?: string;
      videoUrl?: string;
      interactionId?: string;
    },
  ) => {
    const response = await api.patch(
      `/showcase/gallery/${galleryId}/media/${mediaId}`,
      data,
    );
    invalidateShowcaseCaches();
    return response;
  },

  deleteGalleryMedia: async (galleryId: string, mediaId: string) => {
    const response = await api.delete(
      `/showcase/gallery/${galleryId}/media/${mediaId}`,
    );
    invalidateShowcaseCaches();
    return response;
  },

  getGalleryMediaItem: (galleryId: string, mediaId: string) =>
    api.get(`/showcase/gallery/${galleryId}/media/${mediaId}`),

  // Archive/Unarchive endpoints para itens de mídia
  archiveGalleryMedia: async (galleryId: string, mediaId: string) => {
    const response = await api.patch(
      `/showcase/gallery/${galleryId}/media/${mediaId}/archive`,
      {},
    );
    invalidateShowcaseCaches();
    return response;
  },

  unarchiveGalleryMedia: async (galleryId: string, mediaId: string) => {
    const response = await api.patch(
      `/showcase/gallery/${galleryId}/media/${mediaId}/unarchive`,
      {},
    );
    invalidateShowcaseCaches();
    return response;
  },

  // Verifica se o endereço (slug) está livre; falha silenciosa, sem toast.
  isSlugAvailable: async (slug: string): Promise<boolean> => {
    const response = await api.get<{ slug: string; available: boolean }>(`/showcase/slug-available`, {
      params: { slug },
      _suppressErrorMessage: true,
    });
    return response.data.available;
  },

  // Buscar dados completos da vitrine por slug (para exibição pública)
  getVitrineBySlug: (slug: string) => api.get(`/public-showcase/${slug}`),

  // ==================== TEMAS PERSONALIZADOS ====================

  // Criar novo tema
  createTheme: (data: { name: string; description?: string; theme: any }) =>
    api.post("/showcase/themes", data),

  // Listar todos os temas
  getThemes: () => api.get("/showcase/themes"),

  // Buscar tema específico
  getTheme: (themeId: string) => api.get(`/showcase/themes/${themeId}`),

  // Atualizar tema
  updateTheme: (
    themeId: string,
    data: {
      name?: string;
      description?: string;
      theme?: any;
    },
  ) => api.patch(`/showcase/themes/${themeId}`, data),

  // Deletar tema
  deleteTheme: (themeId: string) => api.delete(`/showcase/themes/${themeId}`),
};

// Gerador de Links API Functions
export const createLink = async (data: CreateLinkRequest): Promise<Link> => {
  try {
    const response = await api.post<Link>("/links", data);
    return response.data;
  } catch (error: any) {
    // Erro: mensagem será exibida pelo interceptor (se presente no body)
    throw error;
  }
};

export const getLinks = async (): Promise<LinksResponse> => {
  try {
    const response = await api.get<LinksResponse>("/links");
    return response.data;
  } catch (error: any) {
    // Erro: mensagem será exibida pelo interceptor (se presente no body)
    throw error;
  }
};

export const deleteLink = async (id: string): Promise<void> => {
  try {
    await api.delete(`/links/${id}`);
  } catch (error: any) {
    // Erro: mensagem será exibida pelo interceptor (se presente no body)
    throw error;
  }
};

// Custom Traffic Sources
export const createCustomTrafficSource = async (
  data: CreateCustomTrafficSourceRequest,
): Promise<CustomTrafficSource> => {
  try {
    const response = await api.post<CustomTrafficSource>(
      "/custom-traffic-sources",
      data,
    );
    invalidateCustomTrafficSourcesCache();
    return response.data;
  } catch (error: any) {
    // Erro: mensagem será exibida pelo interceptor (se presente no body)
    throw error;
  }
};

export const getCustomTrafficSources = async (): Promise<
  CustomTrafficSource[]
> => {
  if (
    customTrafficSourcesCache &&
    Date.now() - customTrafficSourcesCacheAt <
      CUSTOM_TRAFFIC_SOURCES_CACHE_TTL_MS
  ) {
    return customTrafficSourcesCache;
  }

  if (pendingCustomTrafficSourcesRequest) {
    return pendingCustomTrafficSourcesRequest;
  }

  pendingCustomTrafficSourcesRequest = api
    .get<CustomTrafficSource[]>("/custom-traffic-sources")
    .then((response) => {
      customTrafficSourcesCache = response.data;
      customTrafficSourcesCacheAt = Date.now();
      return response.data;
    });

  try {
    return await pendingCustomTrafficSourcesRequest;
  } finally {
    pendingCustomTrafficSourcesRequest = null;
  }
};

export const deleteCustomTrafficSource = async (id: string): Promise<void> => {
  try {
    await api.delete(`/custom-traffic-sources/${id}`);
    invalidateCustomTrafficSourcesCache();
  } catch (error: any) {
    // Erro: mensagem será exibida pelo interceptor (se presente no body)
    throw error;
  }
};
